import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface ServiceWorkerLog {
  id: string;
  timestamp: number;
  message: string;
  type: 'info' | 'error' | 'success';
}

interface SharedDataEntry {
  id: string;
  title: string;
  text: string;
  url: string;
  files: Array<{
    name: string;
    type: string;
    size: number;
    data: ArrayBuffer;
  }>;
  timestamp: number;
  expiresAt: number;
}

// Helper functions for IndexedDB operations
async function openShareDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('ShareTargetDB', 2);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains('sharedData')) {
        db.createObjectStore('sharedData', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('swLogs')) {
        db.createObjectStore('swLogs', { keyPath: 'id' });
      }
    };
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function getServiceWorkerLogs(db: IDBDatabase): Promise<ServiceWorkerLog[]> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('swLogs', 'readonly');
    const store = transaction.objectStore('swLogs');
    const request = store.getAll();
    
    request.onsuccess = () => {
      const logs = request.result as ServiceWorkerLog[];
      // Sort by timestamp, most recent first
      logs.sort((a, b) => b.timestamp - a.timestamp);
      resolve(logs);
    };
    request.onerror = () => reject(request.error);
  });
}

async function clearServiceWorkerLogs(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('swLogs', 'readwrite');
    const store = transaction.objectStore('swLogs');
    const request = store.clear();
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

async function getSharedDataEntries(db: IDBDatabase): Promise<SharedDataEntry[]> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('sharedData', 'readonly');
    const store = transaction.objectStore('sharedData');
    const request = store.getAll();
    
    request.onsuccess = () => resolve(request.result as SharedDataEntry[]);
    request.onerror = () => reject(request.error);
  });
}

export function DebugLogs() {
  const navigate = useNavigate();
  const [swLogs, setSwLogs] = useState<ServiceWorkerLog[]>([]);
  const [sharedDataEntries, setSharedDataEntries] = useState<SharedDataEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const db = await openShareDatabase();
      const logs = await getServiceWorkerLogs(db);
      const dataEntries = await getSharedDataEntries(db);
      
      setSwLogs(logs);
      setSharedDataEntries(dataEntries);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load debug data');
    } finally {
      setLoading(false);
    }
  }, []);

  const clearLogs = useCallback(async () => {
    try {
      const db = await openShareDatabase();
      await clearServiceWorkerLogs(db);
      setSwLogs([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear logs');
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-sm max-w-md mx-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2 text-gray-900">Loading Debug Data...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Debug Logs</h1>
            <div className="flex gap-2">
              <button
                onClick={() => void loadData()}
                className="btn btn-info btn-sm"
              >
                Refresh Logs
              </button>
              <button
                onClick={() => void clearLogs()}
                className="btn btn-error btn-sm"
              >
                Clear Logs
              </button>
              <button
                onClick={() => navigate('/')}
                className="btn btn-neutral btn-sm"
              >
                Back to Home
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800">Error: {error}</p>
            </div>
          )}

          {/* Service Worker Logs */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">
              Service Worker Logs ({swLogs.length})
            </h2>
            <div className="bg-blue-50 rounded-lg p-4 max-h-96 overflow-y-auto">
              {swLogs.length === 0 ? (
                <p className="text-gray-500 text-sm">No service worker logs found.</p>
              ) : (
                <div className="space-y-2">
                  {swLogs.map((log) => (
                    <div
                      key={log.id}
                      className={`text-sm font-mono p-2 rounded ${
                        log.type === 'error'
                          ? 'bg-red-100 text-red-800'
                          : log.type === 'success'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <span className="font-semibold">
                          [{new Date(log.timestamp).toLocaleString()}]
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          log.type === 'error'
                            ? 'bg-red-200 text-red-800'
                            : log.type === 'success'
                            ? 'bg-green-200 text-green-800'
                            : 'bg-blue-200 text-blue-800'
                        }`}>
                          {log.type.toUpperCase()}
                        </span>
                      </div>
                      <div className="mt-1 break-words">{log.message}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Shared Data Entries */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">
              Stored Shared Data ({sharedDataEntries.length})
            </h2>
            <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
              {sharedDataEntries.length === 0 ? (
                <p className="text-gray-500 text-sm">No shared data entries found.</p>
              ) : (
                <div className="space-y-4">
                  {sharedDataEntries.map((entry) => (
                    <div key={entry.id} className="bg-white p-4 rounded border">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-semibold text-sm">ID: {entry.id}</span>
                        <span className="text-xs text-gray-500">
                          {new Date(entry.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-sm space-y-1">
                        <div><strong>Title:</strong> {entry.title || 'None'}</div>
                        <div><strong>Text:</strong> {entry.text || 'None'}</div>
                        <div><strong>URL:</strong> {entry.url || 'None'}</div>
                        <div><strong>Files:</strong> {entry.files?.length || 0}</div>
                        {entry.files?.length > 0 && (
                          <div className="ml-4 text-xs text-gray-600">
                            {entry.files.map((file, idx) => (
                              <div key={`${file.name}-${file.size}-${idx}`}>
                                • {file.name} ({file.type}, {(file.size / 1024).toFixed(1)}KB)
                              </div>
                            ))}
                          </div>
                        )}
                        <div><strong>Expires:</strong> {new Date(entry.expiresAt).toLocaleString()}</div>
                        <div><strong>Status:</strong> 
                          <span className={entry.expiresAt < Date.now() ? 'text-red-600' : 'text-green-600'}>
                            {entry.expiresAt < Date.now() ? ' Expired' : ' Active'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-800 mb-2">How to Use:</h3>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• Access this page by going to <code>/debug</code> in your browser</li>
              <li>• Service Worker logs show what happens when you share content</li>
              <li>• Shared Data shows any stored share data (should be empty after successful processing)</li>
              <li>• Use "Refresh" to reload the latest data</li>
              <li>• Use "Clear Logs" to remove all service worker logs</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
} 